import { Box, Typography, styled } from "@mui/material";
import LoadingSpinner from "components/atomics/LoadingSpinner";
import { LeftTileWrapperSC, NoDataContainerSC } from "components/pages/AssetManagement/tileStyles";
import PLTextlink from "components/patternLib/PLTextlink";
import React, { useState } from "react";
import { useRecoilValue } from "recoil";
import translations from "translations";
import { formatDate, formatTimeDistance } from "translations/formatter";
import { deviceIdState } from "../../../assetDetailState";
import AssetPositionMap from "./AssetPositionMap";
import MapLink from "./MapLink";
import PositionHistory from "./PositionHistory";
import { useGetCurrentPosition } from "./useGetCurrentPosition";

const PositionBoxSC = styled("div")(
  () => `
  display: inline-flex;
  margin-top: 1em;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`
);

const LocationDotSC = styled("div")(
  () => `
  width: 10px;
  height: 10px;
  background: #5789A4;
  border-radius: 50%;
  margin-right: 5px;
`
);

const CurrentPositionTile: React.FC<{}> = () => {
  const deviceId = useRecoilValue(deviceIdState);
  const [open, setOpenPositionHistory] = useState(false);
  const { coordinates, loading, address, dateFrom, utcMeasured } = useGetCurrentPosition(deviceId);
  const {
    label,
    noDataMessage,
    firstLocatedDateMessage,
    lastUpdateLocationDateMessage,
  } = translations.pages.assetManagement.assetDetails.currentPositionTile;

  const openPositionHistory = () => {
    setOpenPositionHistory(true);
  };

  return (
    <LeftTileWrapperSC>
      <Typography variant="h4">{label}</Typography>
      <LoadingSpinner sx={{ height: loading ? "45vh" : "fit-content" }} loading={loading ?? false}>
        {coordinates ? (
          <>
            <AssetPositionMap coordinates={coordinates} />
            {address && (
              <>
                <PositionBoxSC>
                  <Box sx={{ display: "inline-flex", alignItems: "center" }}>
                    <LocationDotSC />
                    <Typography variant="copy">
                      {`Coordinates: Latitude ${coordinates[0]}, Longitude ${coordinates[1]}`}
                    </Typography>
                  </Box>
                  <Box>
                    <PLTextlink
                      onClick={openPositionHistory}
                      label="Position history"
                      size="small"
                      bold={false}
                      uppercase={false}
                    />
                  </Box>
                </PositionBoxSC>
                <Box sx={{ marginTop: "0.5%" }}>
                  <Typography variant="copy">
                    {label}: {`Nearby ${address}`}
                  </Typography>
                </Box>
              </>
            )}
            {dateFrom ? (
              <Box sx={{ margin: "0.5% 0" }}>
                <>
                  <Typography variant="copy">{firstLocatedDateMessage} </Typography>
                  <Typography variant="copyBold">{formatDate(new Date(dateFrom))}. </Typography>
                </>
                {utcMeasured && (
                  <>
                    <Typography variant="copy">{lastUpdateLocationDateMessage} </Typography>
                    <Typography variant="copyBold">{formatTimeDistance(new Date(utcMeasured))}</Typography>
                  </>
                )}
              </Box>
            ) : (
              ""
            )}
            <MapLink latitude={coordinates[0]} longitude={coordinates[1]} />
          </>
        ) : (
          !loading && (
            <Box>
              <NoDataContainerSC height="45vh">
                <Typography variant="copy">{noDataMessage}</Typography>
              </NoDataContainerSC>
              <Box sx={{ display: "flex", flexDirection: "row-reverse" }}>
                <PLTextlink
                  onClick={openPositionHistory}
                  label="Position history"
                  size="small"
                  bold={false}
                  uppercase={false}
                  disabled={!deviceId || !utcMeasured}
                />
              </Box>
            </Box>
          )
        )}
        {!!deviceId && (
          <PositionHistory deviceId={deviceId} open={open} setOpenPositionHistory={setOpenPositionHistory} />
        )}
      </LoadingSpinner>
    </LeftTileWrapperSC>
  );
};
export default CurrentPositionTile;
